import { useState, useEffect } from 'react'

interface VirtualCandleProps {
  onComplete: () => void
  candleNumber: number
}

const VirtualCandle = ({ onComplete, candleNumber }: VirtualCandleProps) => {
  const [burnProgress, setBurnProgress] = useState(0)
  const [isLit, setIsLit] = useState(true)
  const [showFlame, setShowFlame] = useState(true)

  useEffect(() => {
    if (!isLit) return

    const duration = 1 * 60 * 1000 // 1 minute in milliseconds
    const interval = 100 // Update every 100ms for smooth animation
    const increment = (interval / duration) * 100

    const timer = setInterval(() => {
      setBurnProgress(prev => {
        const next = prev + increment
        if (next >= 100) {
          clearInterval(timer)
          setShowFlame(false)
          setTimeout(() => {
            onComplete()
          }, 1000) // Small delay after flame goes out
          return 100
        }
        return next
      })
    }, interval)

    return () => clearInterval(timer)
  }, [isLit, onComplete])

  // Calculate candle height based on burn progress
  const candleHeight = Math.max(20, 80 - (burnProgress * 0.6)) // Candle shrinks slightly as it burns
  const flameOpacity = showFlame ? Math.max(0.3, 1 - (burnProgress * 0.01)) : 0

  return (
    <div className="flex flex-col items-center">
      {/* Candle Number */}
      <div className="mb-4">
        <p className="text-peach-500 dark:text-peach-400 text-xs font-light">
          Candle {candleNumber} of 3
        </p>
      </div>

      {/* Candle Container */}
      <div className="relative flex flex-col items-center">
        {/* Flame */}
        {showFlame && (
          <div 
            className="relative mb-1"
            style={{ 
              opacity: flameOpacity,
              transition: 'opacity 0.3s ease-in-out'
            }}
          >
            <div 
              className="w-3 h-6 bg-gradient-to-t from-orange-300 via-yellow-200 to-yellow-100 rounded-full"
              style={{
                animation: 'flicker 1.5s ease-in-out infinite alternate',
                filter: 'blur(0.5px)'
              }}
            />
            {/* Inner flame */}
            <div 
              className="absolute top-1 left-1/2 transform -translate-x-1/2 w-1 h-4 bg-gradient-to-t from-orange-200 to-white rounded-full opacity-80"
              style={{
                animation: 'flicker 1.2s ease-in-out infinite alternate-reverse'
              }}
            />
          </div>
        )}

        {/* Wick */}
        <div className="w-px h-2 bg-peach-800 dark:bg-peach-300 opacity-60" />

        {/* Candle Body */}
        <div 
          className="w-8 bg-peach-100 dark:bg-peach-200 border border-peach-200/50 dark:border-peach-300/50 rounded-b-lg relative overflow-hidden"
          style={{ 
            height: `${candleHeight}px`,
            transition: 'height 0.1s ease-out'
          }}
        >
          {/* Wax drips effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-peach-50 to-peach-200 dark:from-peach-100 dark:to-peach-300 opacity-60" />
          
          {/* Burn line */}
          <div 
            className="absolute top-0 left-0 right-0 h-px bg-peach-300 dark:bg-peach-400 opacity-40"
            style={{
              top: `${burnProgress * 0.6}%`
            }}
          />
        </div>

        {/* Candle Base */}
        <div className="w-12 h-2 bg-peach-200 dark:bg-peach-300 rounded-full opacity-60 mt-1" />
      </div>

      {/* Progress Indicator */}
      <div className="mt-6 w-24">
        <div className="w-full bg-peach-100/30 dark:bg-peach-700/30 rounded-full h-1">
          <div 
            className="bg-peach-300/60 dark:bg-peach-400/60 h-1 rounded-full transition-all duration-100"
            style={{ width: `${burnProgress}%` }}
          />
        </div>
        <p className="text-peach-500 dark:text-peach-400 text-xs font-light text-center mt-2">
          {Math.ceil((100 - burnProgress) * 60 / 100)} seconds remaining
        </p>
      </div>

      {/* CSS for flame animation */}
      <style>{`
        @keyframes flicker {
          0% { transform: scale(1) rotate(-1deg); }
          50% { transform: scale(1.05) rotate(1deg); }
          100% { transform: scale(0.95) rotate(-0.5deg); }
        }
      `}</style>
    </div>
  )
}

export default VirtualCandle